<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import * as THREE from 'three'
import { useThreeScene, type Viewport } from '@/composables/useThreeScene'
import { useGlbLoader } from '@/composables/useGlbLoader'
import {
  useMeshHighlighting,
  type MeshHint,
  type SelectableHint,
} from '@/composables/useMeshHighlighting'
import {
  useArticleTransforms,
  type ArticleTransformHint,
  type StoredArticleTransform,
} from '@/composables/useArticleTransforms'
import { useViewerHighlights } from '@/composables/useViewerHighlights'
import { useViewerSelection, type PartAllocationRequest } from '@/composables/useViewerSelection'
import { useViewerPicking } from '@/composables/useViewerPicking'
import { useExplodeControl } from '@/composables/useExplodeControl'
import { useHintInstances } from '@/composables/useHintInstances'
import { useInstancePicking } from '@/composables/useInstancePicking'
import { useViewerInteraction } from '@/composables/useViewerInteraction'
import { useInstanceAllocationActions } from '@/composables/useInstanceAllocationActions'
import { useFullscreen } from '@/composables/useFullscreen'
import { StepArticleVisibility } from '@/generated/graphql'
import { findInstanceAllocation, type EbomPartInstanceAllocation } from '@/utils/instanceAllocation'
import { scheduleBoundsTrees } from '@/utils/meshPicking'
import { createViewportSync } from '@/utils/viewportSync'
import { viewerStatsEnabled } from '@/utils/viewerDebug'
import ViewerStatusOverlay from '@/components/bop/ViewerStatusOverlay.vue'
import ViewerToolbar from '@/components/bop/ViewerToolbar.vue'
import ViewerInstancePickingBar from '@/components/bop/ViewerInstancePickingBar.vue'
import ViewerInstancePrompt from '@/components/bop/ViewerInstancePrompt.vue'
import ViewerSelectionBar from '@/components/bop/ViewerSelectionBar.vue'
import ViewerExplodeControl from '@/components/bop/ViewerExplodeControl.vue'
import ViewerViewControls from '@/components/bop/ViewerViewControls.vue'
import ViewerExplodeConfirmDialog from '@/components/bop/ViewerExplodeConfirmDialog.vue'
import ViewerPill from '@/components/bop/ViewerPill.vue'
import ViewerStats from '@/components/bop/ViewerStats.vue'

export type { Viewport }

const props = defineProps<{
  glbUrl: string | null
  converting?: boolean
  conversionFailed?: boolean
  dataLoading?: boolean
  visibleMeshHints: MeshHint[] | null
  highlightedMeshHints: MeshHint[]
  savedViewport?: Viewport | null
  canSaveViewport?: boolean
  showWarnings?: boolean
  hideVisibilityToggles?: boolean
  visibilityMode?: StepArticleVisibility
  inStepGroup?: boolean
  selectable?: boolean
  selectableHints?: SelectableHint[]
  ebomPartAllocations?: EbomPartInstanceAllocation[]
  knownArticleNames?: string[]
  colorMap?: Map<string, number>
  activeEbomLabel?: string | null
  editable?: boolean
  stepId?: string
  transformHints?: ArticleTransformHint[]
  editableTransformHints?: ArticleTransformHint[]
  articleTransforms?: StoredArticleTransform[]
  showControlsHelp?: boolean
}>()

const emit = defineEmits<{
  'update:visibilityMode': [value: StepArticleVisibility]
  'save-viewport': [viewport: Viewport]
  'save-transforms': [transforms: StoredArticleTransform[]]
  'unusable-part': [hint: SelectableHint]
  'allocate-part': [request: PartAllocationRequest]
  'save-instances': [hint: SelectableHint, instanceIndexes: number[]]
}>()

const { t } = useI18n()

const root = ref<HTMLDivElement>()
const container = ref<HTMLDivElement>()
const { isFullscreen, toggle: toggleFullscreen } = useFullscreen(root)

const mode = computed(() => props.visibilityMode ?? StepArticleVisibility.AllPrevious)

let skipNextRecenter = false

function onVisibilityModeChange(value: StepArticleVisibility) {
  skipNextRecenter = true
  emit('update:visibilityMode', value)
}

const {
  stats,
  getScene,
  getCamera,
  initRenderer,
  disposeRenderer,
  markSceneChanged,
  flushWorldMatrices,
  addFrameTask,
  addWorldFlushTask,
  fitCamera,
  resetCamera,
  applyViewport,
  refitToBox,
  currentViewport,
  attachGizmo,
  detachGizmo,
  onGizmoChange,
  isGizmoDragging,
} = useThreeScene(container)

const {
  status,
  error,
  progress,
  allMeshes,
  meshAncestorNames,
  syncBatches,
  clearModel,
  loadGlb,
  dispose: disposeLoader,
} = useGlbLoader(getScene)

const transforms = useArticleTransforms(allMeshes, meshAncestorNames, markSceneChanged)
const hintInstances = useHintInstances(allMeshes, meshAncestorNames, transforms.baseWorld)

let cancelBoundsTrees: (() => void) | null = null

const {
  pointerOnPart,
  hoverChanged,
  applyHover,
  clearHover,
  applySelectionHighlight,
  clearSelectionHighlight,
} = useViewerHighlights()

const {
  applyVisibility: applyMeshVisibility,
  recolorHighlights,
  computeHighlightMismatch,
  highlightedVisibleMeshes,
  buildHintResolver,
} = useMeshHighlighting(allMeshes, meshAncestorNames)

let meshHintMap = new Map<THREE.Mesh, SelectableHint | null>()
function hintForMesh(mesh: THREE.Mesh): SelectableHint | null {
  return meshHintMap.get(mesh) ?? null
}

let visibleMeshCache: THREE.Mesh[] | null = null
function visibleMeshes(): THREE.Mesh[] {
  if (!visibleMeshCache) visibleMeshCache = allMeshes.filter((m) => m.visible)
  return visibleMeshCache
}

const interactive = computed(() => !!props.selectable || !!props.editable)

const selectionHighlights = { applySelectionHighlight, clearSelectionHighlight, clearHover }

const selection = useViewerSelection({
  transforms,
  gizmo: { attach: attachGizmo, detach: detachGizmo, setOnChange: onGizmoChange },
  highlights: selectionHighlights,
  hintInstances,
  visibleMeshes,
  hintForMesh,
  editable: () => !!props.editable,
  selectable: () => !!props.selectable,
  allocationFor: (ebomPartId) =>
    findInstanceAllocation(props.ebomPartAllocations ?? [], ebomPartId),
  requestRender: markSceneChanged,
  reportUnusablePart: (hint) => emit('unusable-part', hint),
})

const instancePicking = useInstancePicking({
  hintInstances,
  highlights: selectionHighlights,
  requestRender: markSceneChanged,
})

const interaction = useViewerInteraction({
  selection,
  instancePicking,
  highlights: { hoverChanged, applyHover, clearHover, applySelectionHighlight },
  requestRender: markSceneChanged,
})

const allocationActions = useInstanceAllocationActions({
  selection,
  instancePicking,
  hintInstances,
  enabled: () => !!props.selectable && status.value === 'ready',
  selectableHints: () => props.selectableHints ?? [],
  allocations: () => props.ebomPartAllocations ?? [],
  onAllocatePart: (request) => emit('allocate-part', request),
  onSaveInstances: (hint, instanceIndexes) => emit('save-instances', hint, instanceIndexes),
})

const picking = useViewerPicking({
  container,
  getCamera,
  flushWorldMatrices,
  getPickTargets: visibleMeshes,
  resolveHit: (hit) => {
    const mesh = hit.object as THREE.Mesh
    return mesh?.visible ? mesh : null
  },
  isEnabled: () => interactive.value && status.value === 'ready',
  isBlocked: isGizmoDragging,
  onHover: interaction.hoverMesh,
  onSelect: interaction.selectMesh,
})

let hasSynced = false
let lastSyncedStepId: string | undefined
function syncTransforms() {
  if (status.value !== 'ready') return
  selection.acrossSync(() => {
    const stored = props.articleTransforms ?? []
    const editableHints = props.editableTransformHints ?? []
    transforms.setHints(
      props.transformHints ?? [],
      new Set(editableHints.map((h) => h.articleId)),
      hintInstances.allocatedOccurrenceFilter(editableHints),
    )
    if (props.stepId != null && props.stepId === lastSyncedStepId) {
      transforms.reapplyPoses()
    } else {
      transforms.applyStored(stored, hasSynced)
      if (stored.length > 0) hasSynced = true
    }
    lastSyncedStepId = props.stepId
    resetExplodeState()
  })
  if (instancePicking.active.value) instancePicking.render()
  markSceneChanged()
}

const {
  explodeFactor,
  explodeConfirmOpen,
  onAutoExplode,
  onExplodeEnd,
  confirmExplodeOverride,
  cancelExplodeOverride,
  onResetTransforms,
  resetExplodeState,
} = useExplodeControl(transforms, interaction.clear)

watch(
  [
    () => status.value,
    () => props.transformHints,
    () => props.editableTransformHints,
    () => props.articleTransforms,
  ],
  () => syncTransforms(),
)

function rebuildResolver() {
  meshHintMap = new Map()
  if (!props.selectable) return
  const resolve = buildHintResolver(props.selectableHints ?? [])
  for (const m of allMeshes) {
    const ancestors = meshAncestorNames.get(m)
    meshHintMap.set(m, ancestors ? resolve(ancestors) : null)
  }
}
watch([() => props.selectable, () => props.selectableHints], () => {
  rebuildResolver()
  clearHover()
  markSceneChanged()
})

function applyVisibility() {
  applyMeshVisibility(props.visibleMeshHints, props.highlightedMeshHints, {
    applyViewport,
    refitToBox,
    savedViewport: props.savedViewport,
    skipRecenter: skipNextRecenter,
    knownArticleNames: props.knownArticleNames ? new Set(props.knownArticleNames) : undefined,
    colorMap: props.colorMap,
    meshesOfInstances: hintInstances.meshesOfInstances,
  })
  skipNextRecenter = false
  visibleMeshCache = null
  clearHover()
  clearSelectionHighlight()
  interaction.restoreOverlay()
  markSceneChanged()
}

watch(
  () => props.colorMap,
  () => {
    clearHover()
    clearSelectionHighlight()
    recolorHighlights(props.colorMap)
    interaction.restoreOverlay()
    markSceneChanged()
  },
)

watch([() => props.visibleMeshHints, () => props.highlightedMeshHints], () => {
  if (props.selectable) skipNextRecenter = true
  applyVisibility()
})

const highlightMismatch = computed(() => computeHighlightMismatch(props.highlightedMeshHints))

const showStats = viewerStatsEnabled()

function handleResetCamera() {
  const scene = getScene()
  resetCamera(
    () => scene?.children.find((c): c is THREE.Group => c.type === 'Group'),
    highlightedVisibleMeshes.value,
  )
}

/** Frames just the selected part, which is otherwise hard to find again in a large assembly. */
function zoomToSelection() {
  const meshes = selection.selectionMeshes()
  if (meshes.length === 0) return
  flushWorldMatrices()
  const box = new THREE.Box3()
  for (const m of meshes) box.expandByObject(m)
  refitToBox(box)
}

function onKeyDown(e: KeyboardEvent) {
  const el = document.activeElement
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return
  if (interaction.handleKey(e)) e.preventDefault()
}

function onModelLoaded(group: THREE.Group) {
  hasSynced = false
  instancePicking.stop()
  hintInstances.reset()
  rebuildResolver()
  transforms.captureBase()
  cancelBoundsTrees?.()
  cancelBoundsTrees = scheduleBoundsTrees(allMeshes)
  syncTransforms()
  applyVisibility()
  if (props.savedViewport) applyViewport(props.savedViewport, false)
  else fitCamera(group, highlightedVisibleMeshes.value)
  viewportSync.markApplied(props.stepId, props.savedViewport)
}

function saveViewport() {
  const viewport = currentViewport()
  if (!viewport) return
  emit('save-viewport', viewport)
  // The view bundles the saved camera and the article manipulations made on top of it.
  if (props.editable) emit('save-transforms', transforms.collectForSave())
}

watch(
  () => props.glbUrl,
  (url, oldUrl) => {
    visibleMeshCache = null
    if (url) {
      loadGlb(url, { onLoaded: onModelLoaded })
    } else if (oldUrl && url !== oldUrl) {
      cancelBoundsTrees?.()
      instancePicking.stop()
      clearModel()
      hintInstances.reset()
    }
  },
)

watch(
  () => props.stepId,
  () => instancePicking.stop(),
)

const viewportSync = createViewportSync(applyViewport)
watch([() => props.savedViewport, () => props.stepId, status], () => {
  if (status.value !== 'ready') return
  viewportSync.sync(props.stepId, props.savedViewport)
})

onMounted(() => {
  initRenderer()
  const scene = getScene()
  if (scene) selection.attachTo(scene)
  addFrameTask((now) => {
    transforms.tick(now)
    return transforms.isAnimating()
  })
  addWorldFlushTask(syncBatches)
  picking.attach()
  container.value?.addEventListener('keydown', onKeyDown)
  if (props.glbUrl) {
    lastSyncedStepId = undefined
    loadGlb(props.glbUrl, { onLoaded: onModelLoaded })
  }
})

onBeforeUnmount(() => {
  picking.detach()
  container.value?.removeEventListener('keydown', onKeyDown)
  cancelBoundsTrees?.()
  selection.dispose()
  transforms.dispose()
  disposeLoader()
  disposeRenderer()
})

const {
  activeGizmo,
  hasSelection,
  canTransform,
  canAllocate,
  canAllocateAllInstances,
  selectionNeedsInstanceSelection,
  selectionAllocated,
  selectedInstanceIndex,
  selectedInstanceTotal,
  selectionKey,
  selectionTitle,
  clearSelection,
  onGizmoToggle,
} = selection

const {
  active: pickingInstances,
  target: pickingTarget,
  required: pickingRequired,
  picked: pickedInstances,
  canSave: canSavePickedInstances,
  stop: cancelInstancePicking,
} = instancePicking

const {
  prompt: instanceSelectionPrompt,
  allocateSelection,
  allocateAllInstances,
  pickInstancesFromPrompt,
  pickInstancesFromSelection,
  saveInstances,
} = allocationActions
</script>

<template>
  <div ref="root" class="viewer-root">
    <div
      ref="container"
      class="viewer-canvas"
      :class="{ 'viewer-canvas--selectable': pointerOnPart }"
      tabindex="0"
    />

    <viewer-status-overlay
      :status="status"
      :error="error"
      :progress="progress"
      :has-model="!!glbUrl"
      :converting="converting"
      :conversion-failed="conversionFailed"
      :data-loading="dataLoading"
    />

    <viewer-toolbar
      v-if="status === 'ready'"
      :fullscreen="isFullscreen"
      :show-controls-help="showControlsHelp"
      @reset-camera="handleResetCamera"
      @toggle-fullscreen="toggleFullscreen"
    />

    <transition name="viewer-bar" mode="out-in">
      <viewer-instance-picking-bar
        v-if="status === 'ready' && pickingInstances && pickingTarget"
        :article-number="pickingTarget.articleNumber"
        :required="pickingRequired"
        :picked="pickedInstances.length"
        :can-save="canSavePickedInstances"
        @cancel="cancelInstancePicking"
        @save="saveInstances"
      />
      <viewer-instance-prompt
        v-else-if="status === 'ready' && !hasSelection && instanceSelectionPrompt"
        :key="instanceSelectionPrompt.hint.ebomPartId"
        :article-number="instanceSelectionPrompt.hint.articleNumber"
        :required="instanceSelectionPrompt.required"
        :total="instanceSelectionPrompt.total"
        @pick="pickInstancesFromPrompt"
      />
      <viewer-selection-bar
        v-else-if="status === 'ready' && (editable || hasSelection)"
        :key="hasSelection ? 'selected' : 'idle'"
        :selected="hasSelection"
        :selection-key="selectionKey"
        :title="selectionTitle"
        :instance-index="selectedInstanceIndex"
        :instance-total="selectedInstanceTotal"
        :can-transform="canTransform"
        :active-gizmo="activeGizmo"
        :can-allocate="canAllocate"
        :needs-instance-selection="selectionNeedsInstanceSelection"
        :allocated="selectionAllocated"
        :can-allocate-all-instances="canAllocateAllInstances"
        @gizmo="onGizmoToggle"
        @pick-instances="pickInstancesFromSelection"
        @allocate="allocateSelection"
        @allocate-all-instances="allocateAllInstances"
        @zoom="zoomToSelection"
        @clear="clearSelection"
      />
    </transition>

    <div v-if="status === 'ready'" class="viewer-bottom-bar">
      <viewer-explode-control
        v-if="editable"
        :factor="explodeFactor"
        @explode="onAutoExplode"
        @end="onExplodeEnd"
        @reset="onResetTransforms"
      />

      <viewer-view-controls
        v-if="!hideVisibilityToggles || canSaveViewport"
        :visibility-mode="mode"
        :show-visibility-toggles="!hideVisibilityToggles"
        :in-step-group="inStepGroup"
        :can-save-viewport="canSaveViewport"
        @update:visibility-mode="onVisibilityModeChange"
        @save-viewport="saveViewport"
      />

      <viewer-pill v-if="$slots['model-picker']" class="viewer-model-picker">
        <slot name="model-picker" />
      </viewer-pill>
    </div>

    <v-alert
      v-if="showWarnings && status === 'ready' && highlightMismatch"
      type="warning"
      density="compact"
      variant="tonal"
      class="viewer-alert"
      :text="
        t('component.bopModelViewer.highlightMismatch', {
          highlighted: highlightMismatch.highlighted,
          allocated: highlightMismatch.allocated,
        })
      "
    />

    <viewer-stats
      v-if="showStats && status === 'ready'"
      :stats="stats"
      :mesh-count="allMeshes.length"
      :visible-mesh-count="visibleMeshes().length"
    />

    <div
      v-if="activeEbomLabel && status === 'ready' && !$slots['model-picker']"
      class="viewer-active-ebom"
    >
      <v-icon size="14" class="mr-1">view_in_ar</v-icon>
      {{ t('component.bopModelViewer.showingEbom', { ebomName: activeEbomLabel }) }}
    </div>

    <viewer-explode-confirm-dialog
      v-model="explodeConfirmOpen"
      :attach="isFullscreen ? root : undefined"
      @confirm="confirmExplodeOverride"
      @cancel="cancelExplodeOverride"
    />
  </div>
</template>

<style scoped>
.viewer-root {
  position: relative;
  width: 100%;
  height: 100%;
  background: #f0f0f0;
  border-radius: 4px;
  overflow: hidden;
  -webkit-user-select: none;
  user-select: none;
}

.viewer-root:fullscreen {
  border-radius: 0;
}

.viewer-canvas {
  width: 100%;
  height: 100%;
}

.viewer-canvas:focus-visible {
  outline: none;
}

.viewer-canvas--selectable {
  cursor: pointer;
}

.viewer-bottom-bar {
  position: absolute;
  bottom: 8px;
  left: 8px;
  right: 8px;
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}

.viewer-pill.viewer-model-picker {
  margin-left: auto;
  padding: 2px 4px;
}

.viewer-alert {
  position: absolute;
  top: 8px;
  left: 8px;
  max-width: 340px;
  font-size: 0.8em;
}

.viewer-active-ebom {
  position: absolute;
  bottom: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  padding: 2px 8px;
  background: rgba(255, 255, 255, 0.75);
  color: rgba(0, 0, 0, 0.6);
  border-radius: 12px;
  font-size: 0.75em;
  pointer-events: none;
  max-width: 240px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
